import { handleApiError } from 'api/errors';
import RessourceApi, { Resource, SearchParams } from 'api/ResourceAPI';
import { removeDuplicates } from 'helpers/format';
import { SelectedFiltersList } from 'types/models/SelectedFiltersList';
import User from 'types/entities/User';

export default class UserApiService {
  public static instance: UserApiService;

  public static baseUrl = 'users' as Resource;

  public static baseUrlMe = 'me' as Resource;

  public static baseUrlClients = 'users/clients' as Resource;

  public static baseUrlRole = 'users/have-role' as Resource;

  public static baseUrlDashboardTodofilter = 'multiclient-dashboard/TodoFilter' as Resource;

  public static baseUrlDashboardTodogrid = 'multiclient-dashboard/TodoDashboard' as Resource;

  public static baseUrlTodoSuperFilter = 'kpidashboard/filter-params' as Resource;

  public static baseUrlDashboardGraphKpi = 'kpidashboard/graph-data' as Resource;

  public static baseUrlDashboardGridKpi = 'kpidashboard/table-data' as Resource;

  public static get Instance() {
    // eslint-disable-next-line no-return-assign
    return UserApiService.instance || (UserApiService.instance = new UserApiService());
  }

  public static async fetchUsersByRoleName(
    params: string,
    queryParams = '',
    searchParams?: SearchParams
  ): Promise<User[]> {
    try {
      const { data } = await RessourceApi.getWithAdditionalPath(
        `${this.baseUrlRole}`,
        `${params}${queryParams}`,
        searchParams
      );
      if (!data || !data.datas) return [];
      return data.datas;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async fetchUserRole(): Promise<
    ('consultant' | 'bm' | 'delivery_coordinator' | 'delivery_manager' | 'admin' | 'bu_support' | 'customer')[]
  > {
    try {
      const {
        data: { results },
      } = await RessourceApi.getWithAdditionalPath(this.baseUrlMe, `role-on-customer`, undefined, false);
      return results;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async get(searchParams?: SearchParams) {
    try {
      const { data } = await RessourceApi.get(this.baseUrl, searchParams);
      return data;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async fetchMe() {
    try {
      const { data } = await RessourceApi.get(this.baseUrlMe);
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async getUsersOnCustomer(customer_id: number, searchParams?: SearchParams) {
    try {
      const { data } = await RessourceApi.getWithAdditionalPath(
        this.baseUrl,
        `roles-on-customer/${customer_id}`,
        searchParams
      );
      return data;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async updateRoleOnUser(body: any) {
    try {
      const { data } = await RessourceApi.patch('users/roles', body);
      return data;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async updateUserRole(user_id: number, roleId: number) {
    try {
      const {
        data: { results },
      } = await RessourceApi.post(this.baseUrl, {}, user_id, `assign-role/${roleId}`);
      return results;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async deleteUserRole(user_id: number, action_id?: number) {
    try {
      const options = action_id !== undefined ? { action_id } : undefined;
      const { data } = await RessourceApi.deleteWithAdditionalPath(this.baseUrl, user_id, `unassign-role`, options);
      return data;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async updateClient(id: number, body: any) {
    // try {
    //   const { data } = await RessourceApi.patch('users/clients', body, id);
    //   return data;
    // } catch (error: unknown) {
    //   console.error('updateClient error:', error);
    //   return error;
    // }
    const { data } = await RessourceApi.patch('users/clients', body, id);
    return data;
  }

  public static async fetchById(id: number, params: string) {
    try {
      const { data } = await RessourceApi.getById(this.baseUrl, id, `${params}&size=-1&`);
      return data.catalog;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async getClientsOnCustomer(customerId: number, searchParams?: SearchParams): Promise<User[]> {
    try {
      const { data } = await RessourceApi.get(this.baseUrlClients, { customer_id: customerId, ...searchParams });
      // return data.datas;
      return data;
    } catch (error: unknown) {
      return [];
    }
  }

  public static async createClient(body: any) {
    try {
      const {
        data: { user_client },
      } = await RessourceApi.post(this.baseUrlClients, body);
      return user_client;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async deleteClient(id: number) {
    try {
      const { data } = await RessourceApi.delete(this.baseUrlClients, id);
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async openAccess(user_id: number, email: string, action_id: number) {
    try {
      const {
        data: { results },
      } = await RessourceApi.post(this.baseUrlClients, { email, action_id }, user_id, 'open-access');
      return results;
    } catch (error: unknown) {
      handleApiError(error);
      throw error;
    }
  }

  public static async resetPassword(user_id: number) {
    try {
      const {
        data: { user_client },
      } = await RessourceApi.post(this.baseUrlClients, {}, user_id, 'reset-password');
      return user_client;
    } catch (error: unknown) {
      handleApiError(error);
      throw error;
    }
  }

  public static async deactivateUser(userId: number, requestData: any): Promise<any> {
    try {
      const { data } = await RessourceApi.post(this.baseUrl, requestData, userId, 'anonymize-user');
      return data;
    } catch (error: unknown) {
      handleApiError(error);
      throw error;
    }
  }

  public static async fetchDashboardFilterOrder(): Promise<any> {
    try {
      interface DashboardTodoFilterData {
        clients: any[];
        otps: any[];
        types: any[];
        overdueOptions: any[];
      }
      const response = await RessourceApi.get(this.baseUrlDashboardTodofilter);
      const { data }: { data: DashboardTodoFilterData } = response;
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async fetchDashboardOrderGrid(additionalParams: SearchParams): Promise<any> {
    try {
      const paramsToSend = { ...additionalParams };
      const res = await RessourceApi.get(this.baseUrlDashboardTodogrid, paramsToSend);
      return {
        dashboard_orders: res?.data?.datas,
        totalPage: res?.data.totalPages,
        totalItems: res?.data.totalItems,
        currentPage: res?.data.currentPage,
      };
    } catch (e) {
      return { dashboard_orders: [], totalPage: 0, totalItems: 0, currentPage: 0 };
    }
  }

  public static async fetchParamsFilterKpi(): Promise<any> {
    try {
      const { data } = await RessourceApi.get(this.baseUrlTodoSuperFilter);
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async fetchKpiGraphData(dataType: number, filterSelected: SelectedFiltersList): Promise<any> {
    try {
      const { data } = await RessourceApi.get(this.baseUrlDashboardGraphKpi, {
        data_type: dataType,
        ...filterSelected,
      });
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  public static async fetchKpiGridData(dataType: number, filterSelected: SelectedFiltersList): Promise<any> {
    try {
      const { data } = await RessourceApi.get(this.baseUrlDashboardGridKpi, {
        data_type: dataType,
        ...filterSelected,
      });
      return data;
    } catch (error: unknown) {
      return null;
    }
  }

  // UserApiService.ts
  public static async getKpiExportExcel(dataType: number, searchParams: SearchParams): Promise<any> {
    // Remove pagination params for export
    const { page, size, ...cleanParams } = searchParams;

    const {
      data: {
        xlsx_workflow: { data, filename },
      },
    } = await RessourceApi.get(this.baseUrlDashboardGridKpi, {
      data_type: dataType,
      export: 'xlsx',
      ...cleanParams, // Use cleanParams instead of searchParams
    });

    console.log('Excel data fetched:', { filename, dataLength: data?.length });
    return { data, filename };
  }

  public static fetchPossibleContacts = async (searchParams?: SearchParams): Promise<User[]> => {
    try {
      const getAdmins = this.fetchUsersByRoleName('admin', '', searchParams);
      const getBm = this.fetchUsersByRoleName('bm', '', searchParams);
      const getDM = this.fetchUsersByRoleName('delivery_manager', '', searchParams);
      const getBu_support = this.fetchUsersByRoleName('bu_support', '', searchParams);
      const getDC = this.fetchUsersByRoleName('delivery_coordinator', '', searchParams);
      const [admins, bm, dm, bu_support, dc] = await Promise.all([getAdmins, getBm, getDM, getBu_support, getDC]);

      return removeDuplicates([...admins, ...bm, ...dm, ...bu_support, ...dc]);
    } catch (error: unknown) {
      return [];
    }
  };

  public static async fetchPossiblesDelegues(): Promise<User[]> {
    try {
      const getAdmins = this.fetchUsersByRoleName('admin', '?size=-1');
      const getBm = this.fetchUsersByRoleName('bm', '?size=-1');
      const getDM = this.fetchUsersByRoleName('delivery_manager', '?size=-1');
      const [admins, bm, dm] = await Promise.all([getAdmins, getBm, getDM]);

      return removeDuplicates([...admins, ...bm, ...dm]);
    } catch (error: unknown) {
      return [];
    }
  }

  public static async fetchPossiblesConsultants(): Promise<User[]> {
    try {
      const getAdmins = this.fetchUsersByRoleName('admin', '?size=-1');
      const getBm = this.fetchUsersByRoleName('bm', '?size=-1');
      const getDM = this.fetchUsersByRoleName('delivery_manager', '?size=-1');
      const getDC = this.fetchUsersByRoleName('delivery_coordinator', '?size=-1');
      const getConsultants = this.fetchUsersByRoleName('consultant', '?size=-1');
      const [admins, bm, dm, consultants, tl] = await Promise.all([getAdmins, getBm, getDM, getConsultants, getDC]);

      return removeDuplicates([...admins, ...bm, ...dm, ...tl, ...consultants]);
    } catch (error: unknown) {
      return [];
    }
  }

  public static async fetchPossiblesTeamLeaders(): Promise<User[]> {
    try {
      const getAdmins = this.fetchUsersByRoleName('admin', '?size=-1');
      const getDM = this.fetchUsersByRoleName('delivery_manager', '?size=-1');
      const getDC = this.fetchUsersByRoleName('delivery_coordinator', '?size=-1');
      const getBm = this.fetchUsersByRoleName('bm', '?size=-1');
      const [admins, tl, dm, bm] = await Promise.all([getAdmins, getDM, getDC, getBm]);

      return removeDuplicates([...admins, ...tl, ...dm, ...bm]);
    } catch (error: unknown) {
      return [];
    }
  }

  public static async fetchPossiblesDeliverableTeamLeaders(): Promise<User[]> {
    try {
      const getAdmins = this.fetchUsersByRoleName('admin', '?size=-1');
      const getDM = this.fetchUsersByRoleName('delivery_manager', '?size=-1');
      const getDC = this.fetchUsersByRoleName('delivery_coordinator', '?size=-1');
      const getBM = this.fetchUsersByRoleName('bm', '?size=-1');
      const [admins, dm, tl, bm] = await Promise.all([getAdmins, getDM, getDC, getBM]);

      return removeDuplicates([...admins, ...dm, ...tl, ...bm]);
    } catch (error: unknown) {
      return [];
    }
  }

  public static async getSignature() {
    return RessourceApi.get('me/signature');
  }

  public static async saveSignature(resource: string, data: any, contentType: string) {
    try {
      await fetch(resource, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: data,
      });
    } catch (error) {
      console.error(error);
    }
  }
}
